<template>
  <b-modal
    v-model="dialog"
    :style="{ zIndex: options.zIndex }"
    size="sm"
    @hidden="cancel"
    @ok="agree"
    static
    centered
    content-class="confirm"
    :modal-class="isLoading ? 'loading' : ''"
  >
    <template v-slot:modal-header="{ cancel }">
      <h5 class="modal-title">{{title}}</h5>
      <CloseButton :onClick="cancel" id="confirm-modal" />
    </template>
    <div>
      <div v-show="!!message" class="pa-5" v-html="message"></div>
    </div>
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button
        variant="outline"
        class="button"
        @click.prevent="cancel"
        :disabled="isLoading && options.disableCancelOnLoading"
      >
        {{ options.cancelText }}
      </b-button>
      <b-button
        variant="primary"
        class="button"
        @click.prevent="ok"
        :disabled="isLoading"
      >
        {{ options.submitText }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { cloneDeep } from 'lodash'
import CloseButton from '@components/common/CloseButton.vue'

const defaultOptions = {
  zIndex: 99999,
  submitText: 'Submit',
  cancelText: 'Cancel',
  loadingText: 'Loading...',
  disableCancelOnLoading: false
}

/**
 * This component has a flaw in its' implementation:
 *
 * There are methods `agree` and `cancel`. `cancel` is called on @hidden event, which is a bit wrong.
 * In case when user press confirm (OK) button in the modal it first trigger @ok event, and then @hidden event. This
 * mean that both `agree` and `cancel` methods called in this case, the `agree` method first and then the `cancel`
 * method when the modal actually hidden.
 *
 * I was thinking to refactor it and make it `cancel` method called on @cancel event instead of @hidden event, but
 * unfortunately there are already places in code which rely on this faulty implementation. For example in
 * FinancePayoutsBulkCreation component there is a call of this.$root.$confirm.open method, and in fact that method is
 * called twice when user press confirm button is the confirmation modal. The flawed implementation of this Confirmation
 * Modal, to be more specific the fact that the `cancel` method is called on @hidden event, prevents the confirmation
 * modal to get opened twice. The same problem with calling this.$root.$confirm.open method twice also exists in other
 * places - search for `event.vueTarget.hide('confirmed')` to find them.
 */
export default {
  name: 'ConfirmModal',
  components: {
    CloseButton
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    message: null,
    title: null,
    options: cloneDeep(defaultOptions),
    isLoading: false
  }),
  methods: {
    open (title, message, options) {
      this.dialog = true
      this.title = title
      this.message = message
      this.options = Object.assign(this.options, options)
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    agree () {
      this.resolve(true)
      this.dialog = false
    },
    cancel () {
      this.resolve(false)
      this.dialog = false
      /**
       * This method should be called when modal is actually hidden. At the moment of writing of this comment `cancel`
       * method is called on @hidden event, that is why I'm calling _resetOptions() method here.
       */
      this._resetOptions()
    },
    /**
     * We need to reset `options` because this modal use used in many places across the app. If we won't reset options
     * to their default the subsequent open of the modal will show the modal with options from the previous open (if
     * that open provided some options).
     * @private
     */
    _resetOptions () {
      this.options = cloneDeep(defaultOptions)
      this.isLoading = false
    },
    setLoading (loading) {
      this.isLoading = loading
    }
  }
}
</script>
